import type { ArenaBox, ArenaDefinition, SpawnPoint } from "@arena/shared";

/**
 * Colosseo della Storia (docs/DECISIONS.md, D38): an oval of sand with a
 * moat all around (fall in = ring-out) and the wall of the stands beyond.
 * Six hatches in the middle of the floor, opened by the levers at the two
 * ends; the crowd throws objects onto the sand.
 *
 * The simulation knows only boxes: the oval is cut in bands 0.5 m deep, so
 * the solid floor follows the drawn ellipse within a few centimetres.
 */
const A = 14;
const B = 9.5;
const MOAT = 3.5;
const WALL = 3;
const BAND = 0.5;
/** Hatch grid: 3 × 2 hatches of 2 × 1.5 m in the centre. */
const HATCH_W = 2;
const HATCH_D = 1.5;
const HATCH_COLS = 3;
const HATCH_ROWS = 2;
const HOLE_X = (HATCH_W * HATCH_COLS) / 2;
const HOLE_Z = (HATCH_D * HATCH_ROWS) / 2;

const halfWidth = (a: number, b: number, z: number) => a * Math.sqrt(Math.max(0, 1 - (z / b) ** 2));
const r = (v: number) => Math.round(v * 1000) / 1000;

function floor(): ArenaBox[] {
  const boxes: ArenaBox[] = [];
  for (let z0 = -B; z0 < B - 1e-6; z0 += BAND) {
    const z1 = z0 + BAND;
    const w = r(halfWidth(A, B, (z0 + z1) / 2));
    const y = { min: -1, max: 0 };
    const id = `sand-${boxes.length}`;
    if (z0 >= -HOLE_Z - 1e-6 && z1 <= HOLE_Z + 1e-6) {
      boxes.push({ id: `${id}w`, kind: "floor", min: { x: -w, y: y.min, z: r(z0) }, max: { x: -HOLE_X, y: y.max, z: r(z1) } });
      boxes.push({ id: `${id}e`, kind: "floor", min: { x: HOLE_X, y: y.min, z: r(z0) }, max: { x: w, y: y.max, z: r(z1) } });
    } else {
      boxes.push({ id, kind: "floor", min: { x: -w, y: y.min, z: r(z0) }, max: { x: w, y: y.max, z: r(z1) } });
    }
  }
  return boxes;
}

function hatches(): ArenaBox[] {
  const boxes: ArenaBox[] = [];
  for (let row = 0; row < HATCH_ROWS; row++) {
    for (let col = 0; col < HATCH_COLS; col++) {
      const x0 = -HOLE_X + col * HATCH_W;
      const z0 = -HOLE_Z + row * HATCH_D;
      boxes.push({ id: `hatch-${row * HATCH_COLS + col}`, kind: "hatch", min: { x: x0, y: -0.3, z: z0 }, max: { x: x0 + HATCH_W, y: 0, z: z0 + HATCH_D } });
    }
  }
  return boxes;
}

/** The wall of the stands: one box per band and side, wide enough to leave no gap where the oval turns. */
function wall(): ArenaBox[] {
  const a = A + MOAT;
  const b = B + MOAT;
  const boxes: ArenaBox[] = [];
  // Two more rows past each end of the oval: there the wall runs along X.
  const cap = r(halfWidth(a, b, b - BAND) + 0.8);
  for (let z0 = -b - 2 * BAND; z0 < b + 2 * BAND - 1e-6; z0 += BAND) {
    const z1 = z0 + BAND;
    if (z1 <= -b + 1e-6 || z0 >= b - 1e-6) {
      boxes.push({ id: `wall-${boxes.length}`, kind: "wall", min: { x: -cap, y: -12, z: r(z0) }, max: { x: cap, y: WALL, z: r(z1) } });
      continue;
    }
    const outer = Math.min(halfWidth(a, b, z0), halfWidth(a, b, z1));
    const inner = Math.max(halfWidth(a, b, z0), halfWidth(a, b, z1));
    const x0 = r(outer);
    const x1 = r(inner + 0.8);
    const id = `wall-${boxes.length}`;
    if (x0 < 0.4) {
      boxes.push({ id, kind: "wall", min: { x: -x1, y: -12, z: r(z0) }, max: { x: x1, y: WALL, z: r(z1) } });
    } else {
      boxes.push({ id: `${id}w`, kind: "wall", min: { x: -x1, y: -12, z: r(z0) }, max: { x: -x0, y: WALL, z: r(z1) } });
      boxes.push({ id: `${id}e`, kind: "wall", min: { x: x0, y: -12, z: r(z0) }, max: { x: x1, y: WALL, z: r(z1) } });
    }
  }
  return boxes;
}

const ring = (n: number, sx: number, sz: number, offset = 0): SpawnPoint[] =>
  Array.from({ length: n }, (_, i) => {
    const t = offset + (i / n) * Math.PI * 2;
    return { x: r(Math.cos(t) * sx), y: 0, z: r(Math.sin(t) * sz) };
  });

const HATCH_SOLIDS = hatches();

export const COLOSSEO_ARENA: ArenaDefinition = {
  id: "colosseo",
  schemaVersion: 1,
  displayName: "Colosseo della Storia",
  theme: "colosseo",
  oval: { a: A, b: B, moat: MOAT, wall: WALL },
  killY: -12,
  camera: { yaw: 0, pitch: 0.9, distance: 12.5 },
  solids: [...floor(), ...HATCH_SOLIDS, ...wall()],
  traps: [
    {
      id: "botole",
      levers: [
        { x: -(A - 2.2), y: 0, z: 0 },
        { x: A - 2.2, y: 0, z: 0 },
      ],
      hatches: HATCH_SOLIDS.map((h) => h.id),
    },
  ],
  // Eight spawns on an oval around the hatches.
  spawnPoints: ring(8, 7.5, 5.5, Math.PI / 8),
  // Blue on the south side, Red on the north, facing each other.
  teamSpawns: [
    [
      { x: -1.5, y: 0, z: -5.5 },
      { x: 1.5, y: 0, z: -5.5 },
      { x: -4.5, y: 0, z: -5 },
      { x: 4.5, y: 0, z: -5 },
    ],
    [
      { x: 1.5, y: 0, z: 5.5 },
      { x: -1.5, y: 0, z: 5.5 },
      { x: 4.5, y: 0, z: 5 },
      { x: -4.5, y: 0, z: 5 },
    ],
  ],
  // Where the crowd can throw: two rings, away from the hatches and the edge.
  dropSpots: [...ring(10, 9.5, 6.5), ...ring(6, 5.5, 3.6, Math.PI / 6)],
};
